/* screens/analytics/tableSettings.jsx — общее поведение таблиц раздела «Аналитика»:
   настройка столбцов (скрыть/переставить), «свернуть всё / развернуть всё» для
   иерархий и тумблер «что в ячейке главное — деньги или количество».

   ПОЧЕМУ ОДИН МОДУЛЬ, А НЕ ПО МЕСТУ. Таблиц в разделе семнадцать, включая те,
   что живут в боковых панелях. Каждая из этих трёх настроек, написанная по
   месту, — это семнадцать своих ключей хранения, семнадцать своих клавиатурных
   поведений и семнадцать поводов разъехаться. Сорт и фильтр по столбцу уже
   живут общими (`reportTable.jsx`), это их продолжение.

   ГДЕ ХРАНИТСЯ ВЫБОР. `localStorage`, ключ — ПРОСТРАНСТВО + идентификатор
   таблицы, как у периода (`PeriodBar`, `restorium.analytics_period.<ws>`).
   Пространство в ключе обязательно: у ресторана и у сети разные наборы колонок,
   и «скрыть склад» в одном не значит «скрыть склад» в другом. Не в адресе —
   присланная коллеге ссылка не должна молча перекраивать ему таблицу, и адрес
   упирается в лимит строки запроса gunicorn (4094 Б). Не на сервере — это
   удобство одного человека на одном экране, миграции оно не стоит.

   ЧЕГО ЗДЕСЬ НЕТ. Перетаскивания мышью: порядок меняется кнопками «выше/ниже».
   Drag-and-drop в поповере требует своей модели фокуса и на тач-экране конфликтует
   с прокруткой списка, а выигрыш — ноль: колонок в этих таблицах единицы.
   (Ширину столбца тянут за границу в шапке — ColumnWidths.jsx; здесь только её
   хранение, `widths`, рядом с порядком и видимостью.)

   ГДЕ СТОЯТ ОРГАНЫ. Все они — в ПАНЕЛИ ТАБЛИЦЫ (`TableToolbar`), которая
   рисуется НЕПОСРЕДСТВЕННО над своей таблицей, а не в тулбаре страницы и не
   в подписи под фильтрами. У ABC шестерёнка стояла в верхнем ряду рядом с
   «AI-разбор» и «XLSX», а «развернуть/свернуть» — под фильтрами, за три экрана
   от таблицы: орган, который стоит не у того, чем он управляет, читается как
   настройка страницы. Панель — одна на все семнадцать таблиц раздела, включая
   боковые: слева то, что про СТРОКИ (дерево, счётчик, фильтр по столбцам),
   справа то, что про ПОКАЗ (мера, столбцы). */
import React from 'react';
import { Icon } from '../../components/ui/Icon.jsx';
import { Popover } from '../../components/ui/Popover.jsx';
import { analyticsStorageScope } from '../../lib/api/client.js';
import { hasWidths, widthsApi } from '../../lib/table/columnWidths.js';

const { useState, useMemo, useCallback, useEffect, useRef } = React;

const STORE_PREFIX = 'restorium.table.';

// Область — не само пространство, а analyticsStorageScope(): на демо-стенде
// своя ('demo'), иначе демо читало бы и перетирало столбцы живого пространства
// посетителя (см. lib/api/client.js).
function storeKey(tableId) {
  return STORE_PREFIX + analyticsStorageScope() + '.' + tableId;
}

function readStored(tableId) {
  try {
    const v = JSON.parse(localStorage.getItem(storeKey(tableId)) || 'null');
    return v && typeof v === 'object' ? v : null;
  } catch { return null; }
}

function writeStored(tableId, value) {
  try {
    if (value == null) localStorage.removeItem(storeKey(tableId));
    else localStorage.setItem(storeKey(tableId), JSON.stringify(value));
  } catch { /* приватный режим — просто не запоминаем */ }
}

/* ── МЕТРИКА ЯЧЕЙКИ: деньги или количество ────────────────────────────────────
   В «Движении» и «Инвентаризации» в одной ячейке живут две меры: деньги и
   количество. Кто из них главный — вопрос роли: закупщик читает килограммы,
   собственник рубли.

   ⚠️ Тумблер действует ТОЛЬКО на строках товара. На группах и «Итого»
   количества нет вовсе и быть не может: кг + шт + л не складываются (инвариант
   раздела, см. `analytics.iiko_movement._finish`). Поэтому группы всегда в
   деньгах, а не «в количестве, но пусто» — иначе в «количественном» режиме
   половина таблицы стала бы прочерками. */
export const METRIC_MONEY = 'money';
export const METRIC_QTY = 'qty';

/** Частные меры столбцов после выбора для одного из них.
 *
 * Значение вне пары money/qty значит «как вся таблица» — запись не хранится
 * вовсе (а не пишется null): по числу записей хук решает, есть ли у таблицы
 * настройки, и «столбец как таблица» настройкой не является. Чистая функция —
 * ради теста: хук без DOM не прокрутить. */
export function nextMetricByCol(metricByCol, key, value) {
  const next = { ...(metricByCol || {}) };
  if (value === METRIC_QTY || value === METRIC_MONEY) next[key] = value;
  else delete next[key];
  return next;
}

/** Настройки одной таблицы: видимость и порядок столбцов + главная мера.
 *
 * `columns` — тот же массив, что уходит в `useReportTable`/`ColHead`
 * ({key, label, ...}); столбец с `fixed: true` спрятать нельзя (обычно это
 * колонка названия — без неё строка неопознаваема).
 */
/** Общая мера таблицы из сохранённого значения. Сохранённое побеждает дефолт,
 * каким бы дефолт ни был: прежняя форма `state.metric === QTY ? QTY : default`
 * при дефолте «количество» НИКОГДА не давала «деньги» — тумблер «Инвентаризации»
 * писал выбор в хранилище и тут же его терял (замечание QA 09.09.2026). */
export function resolveMetric(stored, fallback = METRIC_MONEY) {
  return stored === METRIC_QTY || stored === METRIC_MONEY ? stored : fallback;
}

export function useTableSettings(tableId, columns, opts = {}) {
  const { defaultMetric = METRIC_MONEY, dualColumns = false } = opts;
  const [state, setState] = useState(() => readStored(tableId) || {});
  // Смена пространства перемонтирует раздел (RemountOnWorkspace), но ключ
  // хранения читается один раз при инициализации — на всякий случай
  // перечитываем и при смене идентификатора таблицы.
  useEffect(() => { setState(readStored(tableId) || {}); }, [tableId]);

  // next — объект или функция от прежнего состояния (ширины: снимок и правка
  // в одном событии, см. lib/table/columnWidths.widthsApi)
  const patch = useCallback((next) => {
    setState((prev) => {
      const merged = { ...prev, ...(typeof next === 'function' ? next(prev) : next) };
      const empty = !merged.order && !(merged.hidden || []).length
        && !merged.metric && !Object.keys(merged.metricByCol || {}).length
        && !hasWidths(merged.widths);
      writeStored(tableId, empty ? null : merged);
      return merged;
    });
  }, [tableId]);
  const widthsPart = useMemo(() => widthsApi(state.widths, patch), [state.widths, patch]);

  const keys = useMemo(() => columns.map((c) => c.key), [columns]);

  /* Порядок: сохранённый список, ПЕРЕСЕЧЁННЫЙ с текущими столбцами, плюс
     новые в конце. Иначе выпуск, добавивший столбец, не показал бы его никому,
     у кого настройка сохранена, — а удалённый столбец висел бы вечно. */
  const order = useMemo(() => {
    const saved = Array.isArray(state.order) ? state.order : [];
    const known = new Set(keys);
    const kept = saved.filter((k) => known.has(k));
    const rest = keys.filter((k) => !kept.includes(k));
    return [...kept, ...rest];
  }, [state.order, keys]);

  const hidden = useMemo(() => {
    const byKey = new Map(columns.map((c) => [c.key, c]));
    return new Set((state.hidden || []).filter(
      (k) => byKey.has(k) && !byKey.get(k).fixed));
  }, [state.hidden, columns]);

  const visible = useMemo(() => {
    const byKey = new Map(columns.map((c) => [c.key, c]));
    return order.map((k) => byKey.get(k)).filter(Boolean).filter((c) => !hidden.has(c.key));
  }, [order, columns, hidden]);

  const metric = resolveMetric(state.metric, defaultMetric);
  const metricByCol = state.metricByCol || {};
  /* Столбец наследует таблицу, пока его не переключили отдельно. Так «сделать
     всю таблицу количественной» — один клик, а не N кликов по столбцам.
     Общий переключатель при этом СНИМАЕТ частные: человек, нажавший «вся
     таблица — деньги», ждёт денег во всех столбцах, а не в тех, что не трогал
     раньше (иначе один столбец молча остаётся в килограммах, и найти, почему,
     можно только перебрав настройки по одному). */
  const metricFor = useCallback(
    (key) => (metricByCol[key] === METRIC_QTY || metricByCol[key] === METRIC_MONEY
      ? metricByCol[key] : metric),
    [metricByCol, metric]);
  // Явный выбор столбца ('money' | 'qty') или null — «как вся таблица».
  const metricOverride = useCallback(
    (key) => (metricByCol[key] === METRIC_QTY || metricByCol[key] === METRIC_MONEY
      ? metricByCol[key] : null),
    [metricByCol]);

  const api = {
    columns, order, hidden, visible, metric, metricFor, metricOverride, dualColumns,
    ...widthsPart,
    isCustomised: !!(state.order || (state.hidden || []).length || state.metric
      || Object.keys(metricByCol).length || widthsPart.hasWidths),
    toggleHidden(key) {
      const col = columns.find((c) => c.key === key);
      if (!col || col.fixed) return;
      const next = new Set(hidden);
      if (next.has(key)) next.delete(key); else next.add(key);
      patch({ hidden: [...next] });
    },
    move(key, dir) {
      const i = order.indexOf(key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= order.length) return;
      // immovable — столбец стоит там, где задан (месяцы матрицы: хронология);
      // ни его, ни соседа через него не двигаем
      const byKey = new Map(columns.map((c) => [c.key, c]));
      if ((byKey.get(key) || {}).immovable || (byKey.get(order[j]) || {}).immovable) return;
      const next = [...order];
      next[i] = next[j];
      next[j] = key;
      patch({ order: next });
    },
    // дефолтная мера в хранилище не нужна: «как было» — это не настройка
    setMetric(value) { patch({ metric: value === defaultMetric ? null : value, metricByCol: {} }); },
    setMetricFor(key, value) { patch({ metricByCol: nextMetricByCol(metricByCol, key, value) }); },
    reset() { writeStored(tableId, null); setState({}); },
  };
  return api;
}

/** Кнопка «Столбцы»: видимость и порядок столбцов, частные меры столбцов.
 *
 * Подпись словом, а не одна шестерёнка: шестерёнка у таблицы читалась как
 * «настройки отчёта», и её открывали в поисках периода или склада. На узком
 * экране слово прячет CSS, остаётся значок — там ряд и так тесен, а кнопка
 * стоит на своём месте, прямо над таблицей.
 *
 * Общий тумблер меры («вся таблица — деньги / количество») отсюда УШЁЛ в
 * панель таблицы (`MetricToggle`): переключатель, спрятанный в поповере, не
 * находили. Здесь остались исключения из него — частная мера столбца. */
export function TableSettingsButton({ settings, label = 'Столбцы',
                                      title = 'Показать, скрыть и переставить столбцы',
                                      className = '' }) {
  const { columns, order, hidden, dualColumns } = settings;
  const byKey = new Map(columns.map((c) => [c.key, c]));
  const hiddenCount = hidden.size;
  const dualCols = dualColumns ? columns.filter((c) => c.dual) : [];
  const overridden = dualCols.filter((c) => settings.metricOverride(c.key)).length;
  const shown = order.length - hiddenCount;
  return (
    <Popover width={dualCols.length ? 360 : 300} placement="bottom-end" trigger={(
      <button type="button"
        className={'an-ctrl-btn an-ctrl-btn--lbl' + (settings.isCustomised ? ' is-active' : '')
          + (className ? ' ' + className : '')}
        aria-label={label} title={title}>
        <Icon name="settings" size={14} />
        <span className="an-ctrl-btn__txt">{label}</span>
        {hiddenCount > 0 && <span className="an-ctrl-btn__badge">{hiddenCount}</span>}
      </button>
    )}>
      {() => (
        <div className="tset">
          <div className="tset__head">
            <span className="tset__caption">Столбцы таблицы</span>
            {/* «Сбросить» — в шапке, где его ищут по привычке, а не в подвале
                за списком, который на телефоне ещё надо прокрутить. */}
            <button type="button" className="tset__reset"
              disabled={!settings.isCustomised} onClick={settings.reset}
              title="Вернуть столбцы, порядок, ширину и меру как было">
              Сбросить
            </button>
          </div>
          {order.length > 1 ? (
            <>
              <div className="tset__sub t-caption text-dim">
                Показано {shown} из {order.length}
                {dualCols.length > 0 && <> · у столбца может быть своя мера</>}
              </div>
              <ul className="tset__list">
                {order.map((key, i) => {
                  const col = byKey.get(key);
                  if (!col) return null;
                  const off = hidden.has(key);
                  const name = col.label + (col.label2 ? ' ' + col.label2 : '');
                  return (
                    <li key={key} className={'tset__row' + (off ? ' is-off' : '')
                      + (col.fixed ? ' is-fixed' : '')}>
                      <label className="tset__vis">
                        <input type="checkbox" checked={!off} disabled={col.fixed}
                          onChange={() => settings.toggleHidden(key)} />
                        <span className="tset__name" title={col.fixed
                          ? 'Столбец нельзя скрыть: без него строку не опознать'
                          : name}>{name}</span>
                        {col.fixed && <Icon name="lock" size={11} className="tset__lock" />}
                      </label>
                      {col.dual && dualColumns && (
                        /* Родной select, а не пара кнопок: у выбора три состояния
                           (как таблица / деньги / количество), и все три с
                           именами. В 360px ширины две подписанные кнопки плюс
                           имя столбца уже не встают в ряд. */
                        <select className={'tset__met' + (settings.metricOverride(key) ? ' is-set' : '')}
                          aria-label={'Главное значение в столбце «' + name + '»'}
                          title="Главное значение в этом столбце"
                          value={settings.metricOverride(key) || ''}
                          onChange={(e) => settings.setMetricFor(key, e.target.value || null)}>
                          <option value="">как таблица</option>
                          <option value={METRIC_MONEY}>деньги</option>
                          <option value={METRIC_QTY}>кол-во</option>
                        </select>
                      )}
                      {/* immovable (месяцы матрицы «Инвентаризации»): порядок задан
                          данными, кнопок перестановки нет вовсе — кнопки, которые
                          ничего не делают, хуже их отсутствия. */}
                      {!col.immovable && (
                        <span className="tset__ord">
                          <button type="button" className="tset__mv" disabled={i === 0}
                            onClick={() => settings.move(key, -1)}
                            aria-label={'Переместить «' + name + '» выше'} title="Выше">
                            <Icon name="chevron-up" size={12} />
                          </button>
                          <button type="button" className="tset__mv"
                            disabled={i === order.length - 1}
                            onClick={() => settings.move(key, 1)}
                            aria-label={'Переместить «' + name + '» ниже'} title="Ниже">
                            <Icon name="chevron-down" size={12} />
                          </button>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
              {dualCols.length > 0 && (
                <div className="tset__hint t-caption text-dim">
                  {overridden > 0
                    ? overridden + ' ' + plural(overridden, 'столбец', 'столбца', 'столбцов')
                      + ' со своей мерой — они не следуют за общим переключателем.'
                    : 'Столбец со своей мерой перестаёт следовать за общим переключателем над таблицей.'}
                  {' '}На группах и «Итого» всегда деньги: килограммы, штуки и литры
                  не складываются.
                </div>
              )}
            </>
          ) : (
            /* Таблица с единственным столбцом: настраивать нечего, и список
               из одного невыключаемого пункта только сбивал бы. */
            <div className="tset__hint t-caption text-dim">
              Столбцы этой таблицы заданы данными, скрывать и переставлять здесь нечего.
            </div>
          )}
        </div>
      )}
    </Popover>
  );
}

function plural(n, one, few, many) {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

/** Тумблер «главное значение в ячейке — деньги или количество» для ВСЕЙ таблицы.
 *
 * Стоит в панели над таблицей, а не в поповере настроек: это самый частый
 * орган вкладки у закупщика (он читает килограммы), и прятать его за
 * шестерёнку значило заставлять искать. Два состояния с именами — радио,
 * а не переключатель «вкл/выкл»: у «включено» нет имени, а «деньги» есть.
 *
 * Общий выбор СНИМАЕТ частные меры столбцов (см. `useTableSettings.setMetric`);
 * пока они есть, на тумблере стоит метка «частично» — иначе таблица, где один
 * столбец в килограммах при общих деньгах, выглядит сломанной, а не настроенной. */
export function MetricToggle({ settings, className = '' }) {
  if (!settings || !settings.dualColumns) return null;
  const mixed = settings.columns.some((c) => c.dual && settings.metricOverride(c.key));
  const opts = [
    [METRIC_MONEY, 'Деньги', 'credit-card', 'Главное значение в ячейках — сумма'],
    [METRIC_QTY, 'Кол-во', 'scale', 'Главное значение в ячейках — количество (кг, шт, л)'],
  ];
  return (
    <span className={'an-seg an-measure' + (mixed ? ' has-mixed' : '')
      + (className ? ' ' + className : '')}
      role="radiogroup" aria-label="Главное значение в ячейке"
      title={mixed ? 'У части столбцов своя мера — см. «Столбцы»' : undefined}>
      {opts.map(([v, t, ic, tip]) => (
        <button type="button" key={v} role="radio" aria-checked={settings.metric === v}
          className={'an-seg__btn an-ctrl-btn' + (settings.metric === v ? ' is-on' : '')}
          onClick={() => settings.setMetric(v)} title={tip} value={v}>
          <Icon name={ic} size={13} />
          <span className="an-seg__txt">{t}</span>
        </button>
      ))}
      {mixed && <span className="an-measure__mixed" aria-hidden="true" />}
    </span>
  );
}

/** ПАНЕЛЬ ТАБЛИЦЫ — ряд органов управления непосредственно над таблицей.
 *
 * Слева — про СТРОКИ: дерево (`tree` из `useTreeExpansion` или адаптер того же
 * вида), подпись таблицы (`title`), счётчик строк (`count`/`countUnit`),
 * фильтр по столбцам (`colState` из `useReportTable` — сколько активно и
 * «сбросить»), произвольная заметка (`note`). Справа — про ПОКАЗ: мера
 * (`MetricToggle`, только у таблиц с двумя мерами), «Столбцы»
 * (`TableSettingsButton`), а перед ними — `children` вызывающего (сортировка
 * матрицы, сброс фильтров и т.п.).
 *
 * Кнопка «Столбцы» не рисуется, когда настраивать нечего (один столбец и нет
 * частных мер): пустой поповер только сбивает. */
export function TableToolbar({ tree, settings, colState, title, count, countUnit,
                               note, children, className = '', ariaLabel = 'Управление таблицей' }) {
  const dualCols = settings && settings.dualColumns ? settings.columns.filter((c) => c.dual) : [];
  const showSettings = !!settings && (settings.order.length > 1 || dualCols.length > 0);
  const filtered = colState && colState.activeCount > 0;
  return (
    <div className={'an-tt an-toolbar' + (className ? ' ' + className : '')}
      role="toolbar" aria-label={ariaLabel}>
      <div className="an-tt__lead">
        <TreeExpandButtons tree={tree} />
        {title != null && <span className="an-tt__title">{title}</span>}
        {count != null && (
          <span className="an-tt__count" title="Строк в таблице под текущими фильтрами">
            <span className="num">{count}</span>
            {countUnit ? ' ' + (typeof countUnit === 'function' ? countUnit(count) : countUnit) : ''}
          </span>
        )}
        {filtered && (
          /* Фильтр по столбцу убирает строки молча; счётчик и «сбросить» —
             единственное, что отличает узкую выборку от «данных больше нет». */
          <button type="button" className="an-tt__filter" onClick={colState.clearAll}
            title="Снять фильтры и сортировку по столбцам">
            <Icon name="filter" size={12} />
            <span>{colState.activeCount} {plural(colState.activeCount, 'фильтр', 'фильтра', 'фильтров')}</span>
            <Icon name="close" size={12} className="an-tt__filter-x" />
          </button>
        )}
        {note}
      </div>
      <div className="an-tt__tools">
        {children}
        <MetricToggle settings={settings} />
        {showSettings && <TableSettingsButton settings={settings} />}
      </div>
    </div>
  );
}

/* ── ИЕРАРХИЯ: свернуть всё / развернуть всё ──────────────────────────────────
   ⚠️ ПОЛЯРНОСТЬ ХРАНЕНИЯ. Раскрытые узлы держатся МНОЖЕСТВОМ РАСКРЫТЫХ, а не
   «множеством свёрнутых». Разница видна ровно в одном месте — когда приходят
   новые данные: при хранении свёрнутых любой новый узел оказывается раскрытым,
   и «свернуть всё» перестаёт держаться после смены периода. */
export function useTreeExpansion({ allKeys, topKeys, resetKey, defaultKeys }) {
  const [expanded, setExpanded] = useState(null);
  const seen = useRef('');

  /* Дефолт считаем ОДИН раз на приход данных, но пересчитываем при смене
     resetKey (период, фильтры): иначе набор ключей меняется, а раскрытие
     остаётся от прошлой выборки и половина дерева стоит свёрнутой без причины.

     `defaultKeys` задаёт вызывающий, и это не придирка к API. Когда у «Движения»
     появился уровень СКЛАДА, «раскрыть верхний уровень» стало значить «показать
     три склада» — вкладка открывалась экраном из трёх строк, без единого товара
     (поймано живым прогоном). Сколько уровней раскрыть по умолчанию, знает
     только тот, кто знает форму своего дерева. */
  useEffect(() => {
    const stamp = String(resetKey);
    if (!allKeys || !allKeys.length) return;
    if (expanded != null && seen.current === stamp) return;
    seen.current = stamp;
    setExpanded(new Set(defaultKeys || topKeys || []));
  }, [allKeys, topKeys, resetKey, defaultKeys, expanded]);

  const isOpen = useCallback((key) => !expanded || expanded.has(key), [expanded]);
  const toggle = useCallback((key) => {
    setExpanded((prev) => {
      const next = new Set(prev || []);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);
  const expandAll = useCallback(() => setExpanded(new Set(allKeys || [])), [allKeys]);
  const collapseAll = useCallback(() => setExpanded(new Set()), []);
  const openCount = expanded ? expanded.size : (topKeys || []).length;
  return {
    expanded, isOpen, toggle, expandAll, collapseAll,
    allExpanded: openCount >= (allKeys || []).length && (allKeys || []).length > 0,
    allCollapsed: openCount === 0,
    hasTree: (allKeys || []).length > (topKeys || []).length,
  };
}

/** Пара кнопок «развернуть всё / свернуть всё» для иерархической таблицы.
 *
 * Одна СПАРЕННАЯ группа с подписями, а не два одиноких квадрата со стрелками:
 * два квадрата рядом читались как «стрелки куда-то», и что они про дерево
 * таблицы, узнавали наведением. Глифы — зеркальная пара одного шеврона (вниз /
 * вверх), те же, что у самих строк дерева, где вниз уже значит «раскрыто».
 * На узком экране подписи прячет CSS — остаются значки в общей рамке. */
export function TreeExpandButtons({ tree, className = '' }) {
  if (!tree || !tree.hasTree) return null;
  return (
    <span className={'an-seg an-treebtns' + (className ? ' ' + className : '')}
      role="group" aria-label="Дерево таблицы">
      <button type="button" className="an-seg__btn an-ctrl-btn" onClick={tree.expandAll}
        disabled={tree.allExpanded}
        aria-label="Развернуть все группы" title="Развернуть все группы">
        <Icon name="chevron-down" size={14} />
        <span className="an-seg__txt">Развернуть</span>
      </button>
      <button type="button" className="an-seg__btn an-ctrl-btn" onClick={tree.collapseAll}
        disabled={tree.allCollapsed}
        aria-label="Свернуть все группы" title="Свернуть все группы">
        <Icon name="chevron-up" size={14} />
        <span className="an-seg__txt">Свернуть</span>
      </button>
    </span>
  );
}

/** Ячейка с двумя мерами: главная крупно, вторая подписью.
 *
 * Пустая ячейка — это НЕ ноль: «движения такого типа не было» и «было на ноль»
 * разные факты, и рисовать 0 там, где ничего не происходило, значит врать. */
export function DualValue({ money, qty, unit, metric = METRIC_MONEY,
                            moneyFmt, qtyFmt }) {
  const hasQty = qty != null && qtyFmt;
  const qtyText = hasQty ? (qtyFmt(qty) + (unit ? ' ' + unit : '')) : '';
  const primaryIsQty = metric === METRIC_QTY && hasQty;
  return (
    <>
      <span className="an-dual__val">
        {primaryIsQty ? qtyText : moneyFmt(money)}
      </span>
      {(primaryIsQty ? money != null : hasQty) && (
        <span className="an-dual__sub">
          {primaryIsQty ? moneyFmt(money) : qtyText}
        </span>
      )}
    </>
  );
}
